/**
 * Date and Timezone Utilities
 * Handles timezone detection and conversion for user-friendly timestamp display
 */

/**
 * Get user's timezone with GMT+3 fallback
 * @returns {string} User's timezone (e.g., 'Europe/Istanbul', 'America/New_York')
 */
export const getUserTimezone = () => {
  try {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return detectedTimezone;
  } catch (error) {
    return 'Europe/Istanbul'; // GMT+3 fallback
  }
};

/**
 * Get timezone offset string for display (e.g., "GMT+3", "GMT-5")
 * @param {string} timezone - The timezone identifier
 * @returns {string} Formatted offset string
 */
export const getTimezoneOffset = (timezone = null) => {
  const tz = timezone || getUserTimezone();
  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'longOffset'
    });
    
    const parts = formatter.formatToParts(date);
    const timeZoneName = parts.find(part => part.type === 'timeZoneName');
    
    if (timeZoneName && timeZoneName.value) {
      return timeZoneName.value; // Returns format like "GMT+3"
    }
    
    // Fallback: calculate offset manually
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const localDate = new Date(date.toLocaleString('en-US', { timeZone: tz }));
    const offsetHours = (localDate - utcDate) / (1000 * 60 * 60);
    
    if (offsetHours === 0) return 'GMT';
    const sign = offsetHours > 0 ? '+' : '';
    return `GMT${sign}${offsetHours}`;
    
  } catch (error) {
    return 'GMT+3'; // Default fallback
  }
};

/**
 * Format UTC timestamp to user's local time with timezone indicator
 * @param {string|Date} utcTimestamp - UTC timestamp or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted local time string
 */
export const formatLocalTime = (utcTimestamp, options = {}) => {
  if (!utcTimestamp) return 'Unknown time';
  
  const {
    includeTimezone = true,
    includeDate = true,
    includeTime = true,
    format = 'dashboard' // 'dashboard' for YYYY-MM-DD HH:mm:ss format
  } = options;
  
  const timezone = getUserTimezone();
  
  try {
    const date = new Date(utcTimestamp);
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    if (format === 'dashboard') {
      // Format as YYYY-MM-DD HH:mm:ss in user's timezone
      // Use a more reliable approach to convert to user's timezone
      const formatter = new Intl.DateTimeFormat('sv-SE', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      const formattedParts = formatter.formatToParts(date);
      const dateMap = {};
      formattedParts.forEach(part => {
        dateMap[part.type] = part.value;
      });
      
      return `${dateMap.year}-${dateMap.month}-${dateMap.day} ${dateMap.hour}:${dateMap.minute}:${dateMap.second}`;
    }
    
    // Legacy format for other uses
    let formatOptions = {
      timeZone: timezone,
    };
    
    if (includeDate) {
      formatOptions.month = '2-digit';
      formatOptions.day = '2-digit';
      formatOptions.year = 'numeric';
    }
    
    if (includeTime) {
      formatOptions.hour = '2-digit';
      formatOptions.minute = '2-digit';
      formatOptions.hour12 = true;
    }
    
    let formatted = date.toLocaleString('en-US', formatOptions);
    
    // Add timezone indicator if requested
    if (includeTimezone) {
      const offset = getTimezoneOffset(timezone);
      formatted += ` ${offset}`;
    }
    
    return formatted;
    
  } catch (error) {
    return 'Format error';
  }
};

/**
 * Format time for usage displays (optimized for dashboard/billing)
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @returns {string} Formatted time for usage display
 */
export const formatUsageTime = (utcTimestamp) => {
  return formatLocalTime(utcTimestamp, {
    includeTimezone: false,
    includeDate: true,
    includeTime: true,
    format: 'dashboard'
  });
};

/**
 * Format relative time (e.g., "2 hours ago", "Yesterday")
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (utcTimestamp) => {
  if (!utcTimestamp) return 'Unknown time';
  
  try {
    const date = new Date(utcTimestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    // For older dates, show absolute time
    return formatUsageTime(utcTimestamp);
    
  } catch (error) {
    return formatUsageTime(utcTimestamp);
  }
};

/**
 * Check if user is in GMT+3 timezone
 * @returns {boolean} True if user is in GMT+3
 */
export const isUserInGMTPlus3 = () => {
  const offset = getTimezoneOffset();
  return offset === 'GMT+3';
};

/**
 * Get timezone display name for column headers
 * @returns {string} Display name like "Time (GMT+3)" or "Time (America/New_York)"
 */
export const getTimezoneDisplayName = () => {
  try {
    const timezone = getUserTimezone();
    const offset = getTimezoneOffset(timezone);
    
    // If we can get a clean GMT offset, use it
    if (offset && offset.startsWith('GMT')) {
      return `Time (${offset})`;
    }
    
    // Otherwise, try to get a friendly name
    const formatter = new Intl.DateTimeFormat('en', { 
      timeZone: timezone, 
      timeZoneName: 'short' 
    });
    const parts = formatter.formatToParts(new Date());
    const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;
    
    if (timeZoneName) {
      return `Time (${timeZoneName})`;
    }
    
    // Fallback to timezone identifier
    return `Time (${timezone})`;
    
  } catch (error) {
    return 'Time (GMT+3)'; // Safe fallback
  }
};

/**
 * Development/Debug utility to test timezone functions
 */
export const debugTimezoneInfo = () => {
  // Debug function removed for production
};